import React, { useEffect, useMemo, useState } from 'react';
import { useCartContext } from './CartContext';
import Icon from '@components/atoms/Icon';
import GoalMarker from '@components/atoms/GoalMarker';
import GwpProduct from './GwpProduct';

interface GiftTierProps {
  subtotal: { amount: string; currencyCode: string };
}

const GiftTier: React.FC<GiftTierProps> = ({ subtotal }) => {
  const { cart, data } = useCartContext();
  const [isOpen, setIsOpen] = useState(false);
  const subtotalAmount = parseFloat(subtotal.amount);
  const gwpData = useMemo(() => data.gwp || [], [data.gwp]);

  // Filter out discount items
  const gwpFilteredData = useMemo(() => {
    return gwpData.filter((item) => {
      if (window.Shopify.shop === 'pitviper.myshopify.com' && window.DEPT.localization.countryCode !== "US" && item.freeShipping) return false;
      return true;
    });
  }, [gwpData]);

  // Calculate the maximum threshold to determine progress
  const maxThreshold = useMemo(() => {
    const numericThresholds = gwpFilteredData.map((item) => parseFloat(item.threshold.replace('$', '')));
    return Math.max(...numericThresholds, 0);
  }, [gwpFilteredData]);

  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    const progress = (subtotalAmount / maxThreshold) * 100;
    return Math.min(progress, 100); // Cap at 100%
  }, [subtotalAmount, maxThreshold]);

  // Check if a specific gift is already in the cart
  const isGiftInCart = useMemo(
    () => (product: { variants: Array<{ id: number }> }) => {
      const variantIds = product.variants.map(
        (variant) => `gid://shopify/ProductVariant/${variant.id}`
      );

      return (cart.state.lines || []).some((line) => {
        const hasGwpType = line.attributes.some(
          (attr) => attr.key === 'type' && attr.value === 'gwp'
        );
        const isMatchingVariant = variantIds.includes(line.merchandise.id);
        return hasGwpType && isMatchingVariant;
      });
    },
    [cart.state.lines]
  );

  // Remove GWP items if subtotal drops below threshold
  useEffect(() => {
    const validateGwpEligibility = () => {
      const cartLines = cart.state.lines;
      if (!cartLines || !Array.isArray(cartLines)) return;
  
      const variantIdsToRemove: string[] = []; // Collect variant IDs to remove in one batch
  
      gwpFilteredData.forEach((tier) => {
        const threshold = parseFloat(tier.threshold.replace('$', ''));
  
        // Check if the product is in the cart and if the subtotal drops below the threshold
        if (tier.product && subtotalAmount < threshold) {
          tier.product.variants.forEach((variant) => {
            const variantId = `gid://shopify/ProductVariant/${variant.id}`;
  
            // Check if this variant is in the cart
            const isVariantInCart = cartLines.some(
              (line) =>
                line.merchandise.id === variantId &&
                line.attributes?.some((attr) => attr.key === 'type' && attr.value === 'gwp')
            );
  
            if (isVariantInCart && !variantIdsToRemove.includes(variantId)) {
              variantIdsToRemove.push(variantId); // Add to batch for removal
            }
          });
        }
      });
  
      // Perform batch removal if there are items to remove
      if (variantIdsToRemove.length > 0) {
        cart.remove(variantIdsToRemove);
      }
    };
  
    validateGwpEligibility();
  }, [cart, subtotalAmount, gwpFilteredData, cart.state.lines]);
  

  // Available GWP products based on threshold
  const availableGwpProducts = useMemo(
    () =>
      gwpFilteredData.filter((tier) => {
        const threshold = parseFloat(tier.threshold.replace('$', ''));
        return subtotalAmount >= threshold;
      }),
    [gwpFilteredData, subtotalAmount]
  );

  // Determine if there are no available or visible GWP products
  const noGwpVisible = useMemo(() => {
    return availableGwpProducts.every((tier) => !tier.product || isGiftInCart(tier.product));
  }, [availableGwpProducts, isGiftInCart]);

  // Toggle button text logic
  const toggleButtonText = useMemo(() => {
    if (noGwpVisible) {
      return isOpen ? window.DEPT.Strings.cart.close : window.DEPT.Strings.cart.open;
    }
    return window.DEPT.Strings.cart.addYourFreeGifts;
  }, [noGwpVisible, isOpen]);

  // Toggle details visibility
  const toggleDetails = () => setIsOpen((prev) => !prev);

  //Find the free shipping tier for copy above progress bar
  const freeShippingTier = gwpFilteredData.find(tier => tier.freeShipping);
  const freeShippingThreshold = freeShippingTier ? parseFloat(freeShippingTier.threshold.replace('$', '')) : null;

  // Sort tiers by threshold to find next tier for copy above progress bar
  const sortedTiers = [...gwpFilteredData].sort((a, b) =>
    parseFloat(a.threshold.replace('$', '')) - parseFloat(b.threshold.replace('$', ''))
  );

  // Find the next tier to unlock for copy above progress bar
  const nextTier = sortedTiers.find(tier => {
    const threshold = parseFloat(tier.threshold.replace('$', ''));
    return threshold > subtotalAmount;
  });

  // Generate dynamic messaging above progress bar
  const getTierMessage = () => {
    // If we haven't reached free shipping yet and it exists
    if (freeShippingThreshold && subtotalAmount < freeShippingThreshold) {
      const amountNeeded = (freeShippingThreshold - subtotalAmount).toFixed(2);
      return `Add $${amountNeeded} to unlock free shipping.`;
    }

    // If there's a next tier to reach
    if (nextTier) {
      const threshold = parseFloat(nextTier.threshold.replace('$', ''));
      const amountNeeded = (threshold - subtotalAmount).toFixed(2);
      return `Add $${amountNeeded} to unlock another reward.`;
    }

    // All tiers unlocked
    return "Sick shopping, you've unlocked all rewards!";
  };

  if (!gwpFilteredData.length) return null;

  return (
    <div className="bg-grey-5">
      <div className="mb-8 mt-2 flex justify-center body-xs font-semibold">{getTierMessage()}</div>

      {/* Progress Bar */}
      <div className="relative w-[94%] m-auto h-2 bg-grey-13 rounded-sm mb-16">
        {gwpFilteredData.map((tier, index) => {
          const threshold = parseFloat(tier.threshold.replace('$', ''));
          const position = `${Math.min((threshold / maxThreshold) * 100, 100)}%`;
          const isLast = index === gwpFilteredData.length - 1;
          return (
            <GoalMarker
              key={`goal-marker-${index}`}
              position={position}
              isLast={isLast}
              labelName={tier.label}
              tierThreshold={tier.threshold}
              tierIcon={tier.icon}
            />
          );
        })}
        <div
          className="h-full bg-gradient-primary rounded-sm"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      {/* Gift Section */}
      {!noGwpVisible && (
        <div className={`mb-3 ${!isOpen && 'bg-gradient-primary rounded-xl p-0.5'}`}>
          <div
            className={`overflow-hidden  bg-grey-5 ${
              isOpen ? 'max-h-240 opacity-100 transition-all duration-300' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="flex flex-col gap-2.5 pb-2.5">
              {gwpData.map((tier, index) => {
                if (
                  !!tier.discount &&
                  !!tier.discountThreshold &&
                  Number(cart.state.subtotal?.amount) >= tier.discountThreshold
                ) {
                  return (
                    <div
                      key={`discount-${index}`}
                      className="border border-green-2 border-dashed bg-blue-3 rounded flex items-center"
                    >
                      <div className="body-sm border-r border-green-2 border-dashed p-5 font-medium">
                        {`${tier.discountValue}% off`}
                      </div>
                      <div className="flex-1 flex items-center justify-between gap-2.5 p-5">
                        <span className="body-xs">{tier.discountMessage}</span>
                      </div>
                    </div>
                  );
                }
                return null;
              })}


              {availableGwpProducts.map((tier) =>
                tier.product && !isGiftInCart(tier.product) ? (
                  <GwpProduct
                    key={tier.product.id}
                    product={tier.product}
                    excludedVariants={tier.excludedVariants || []}
                  />
                ) : null
              )}
            </div>
          </div>

          {/* Toggle Button */}
          <div className={`flex cursor-pointer items-center justify-center ${!isOpen && 'bg-grey-13 rounded-xl p-1'}`} onClick={toggleDetails}>
            <span className="body-xxs">{toggleButtonText}</span>
            <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            <Icon name="chevron-down" className="text-black w-4 h-4" />
          </span>
          </div>
        </div>
      )}

    </div>
  );
};

export default GiftTier;
